// src/kisiye-ozel-rapor/src/pages/AylikIsg.js
import React, { useMemo, useRef, useState } from "react";
import { db } from "../../../firebaseConfig";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import {
  Document,
  Packer,
  Paragraph,
  Table as DocxTable,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  WidthType,
  ImageRun,
} from "docx";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";



/* ======== Renkler ve stiller ======== */
const COLORS = {
  headerBlue: "#e6f0ff",
  dashedBorder: "1px dashed #000",
  kiremit: "#c0492b33",
  softYesil: "#22c55e22",
  greenHeader: "#10b981",
  greenTotal: "#86efac",
  tesisSolved: "#f59e0b",
  tesisOpen: "#fbbf24",
  agingLt30: "#60a5fa",
  aging30_59: "#34d399",
  aging60_89: "#f59e0b",
  aging90p: "#ef4444",
  grid: "#e5e7eb",
  panel: "#fff",
  panelBorder: "#e5e7eb",
  legendBoxBorder: "#cbd5e1",
};

const pad2 = (n) => String(n).padStart(2, "0");
const monthKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

const isISO = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || "");
const toDateSafe = (s, isEnd = false) => {
  if (!isISO(s)) return null;
  const [Y, M, D] = s.split("-").map(Number);
  const d = new Date(
    Y,
    M - 1,
    D,
    isEnd ? 23 : 0,
    isEnd ? 59 : 0,
    isEnd ? 59 : 0,
    isEnd ? 999 : 0
  );
  return Number.isFinite(d.getTime()) ? d : null;
};
const toTimestamp = (s, isEnd = false) => {
  const d = toDateSafe(s, isEnd);
  return d ? Timestamp.fromDate(d) : null;
};
const tsToMs = (ts) =>
  ts?.toDate?.().getTime?.() ??
  (typeof ts?.seconds === "number" ? ts.seconds * 1000 : NaN);

const inRange = (ts, from, to) => {
  const ms = tsToMs(ts);
  if (!Number.isFinite(ms)) return false;
  return ms >= from.getTime() && ms <= to.getTime();
};
const daysBetween = (a, b) =>
  Math.floor((b.getTime() - a.getTime()) / 86400000);

function toDateMaybe(val) {
  if (!val) return null;
  if (val?.toDate) return val.toDate();
  const s = String(val).replaceAll("/", ".").replaceAll("-", ".");
  let m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(val))) return new Date(String(val));
  return null;
}
const fmt0 = (n) =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(n || 0);
const titleCaseTR = (s = "") =>
  (s || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => (w[0] ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");

const toSlugTR = (str = "") => {
  const map = {
    Ç: "c",
    Ö: "o",
    Ş: "s",
    İ: "i",
    I: "i",
    Ğ: "g",
    Ü: "u",
    ç: "c",
    ö: "o",
    ş: "s",
    ı: "i",
    ğ: "g",
    ü: "u",
  };
  const replaced = (str || "").replace(
    /[ÇÖŞİIĞÜçöşığü]/g,
    (ch) => map[ch] || ch
  );
  return replaced
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/* ============ Yardımcı: personelin seçili aralıkta çalıştığı gün sayısı ============ */
function workedDaysForPersonRanges(p, rangeStart, rangeEnd) {
  const tarih = p?.tarih || {};
  let total = 0;
  for (let i = 1; i <= 9; i++) {
    const g = toDateMaybe(tarih[`giris${i}`]);
    const c = toDateMaybe(tarih[`cikis${i}`]);
    if (!g && !c) continue;
    const start = g
      ? new Date(Math.max(g.getTime(), rangeStart.getTime()))
      : rangeStart;
    const end = c
      ? new Date(Math.min(c.getTime(), rangeEnd.getTime()))
      : rangeEnd;
    if (start <= end) total += daysBetween(start, end) + 1;
  }
  return total;
}

/* ============ Yardımcı: personelin toplam çalıştığı gün sayısı (tüm geçmiş) ============ */
function totalWorkedDaysAllRanges(p) {
  const tarih = p?.tarih || {};
  const today = new Date();
  let total = 0;
  for (let i = 1; i <= 9; i++) {
    const g = toDateMaybe(tarih[`giris${i}`]);
    const c = toDateMaybe(tarih[`cikis${i}`]);
    if (!g) continue;
    const end = c || today;
    const diff = Math.floor((end.getTime() - g.getTime()) / 86400000);
    if (diff > 0) total += diff;
  }
  return total;
}

export default function AylikIsg() {
  const today = new Date();

  // Tarih filtreleri
  const [fromStr, setFromStr] = useState("2025-01-01");
  const [toStr, setToStr] = useState(
    `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(
      today.getDate()
    )}`
  );

  // Tesis filtresi
  const [tesisSel, setTesisSel] = useState("TÜMÜ");

  // Rapor durumu
  const [started, setStarted] = useState(false);

  // Firestore’dan gelen ana veriler
  const [personellers, setPersoneller] = useState([]);
  const [issues, setIssues] = useState([]);
  const [kazalar, setKazalar] = useState([]);

  // Termin / başlık eşleştirmeleri
  const [terminMap, setTerminMap] = useState({});
  const [titleMap, setTitleMap] = useState({});

  // DOCX için rapor alanı referansı
  const reportRef = useRef(null);
  // Grafikleri tek tek yakalamak için
  const eksiklikChartRef = useRef(null);
  const agingChartRef = useRef(null);
  const kazaAyChartRef = useRef(null);

  // Bir eksikliğin termin kategorisini bulma
  const getCategoryForIssue = (it) => {
    if (it?.terminCategoryId && terminMap[it.terminCategoryId]) {
      return terminMap[it.terminCategoryId];
    }

    const slug = toSlugTR((it.issue || "").trim());
    const mapDoc = slug ? titleMap[slug] : null;

    if (mapDoc && mapDoc.categoryId && terminMap[mapDoc.categoryId]) {
      return terminMap[mapDoc.categoryId];
    }
    return null;
  };

  const handleStart = async () => {
    // BURADAN SONRASI SENDEKİYLE AYNI KALSIN

    const dFrom = toDateSafe(fromStr, false);
    const dTo = toDateSafe(toStr, true);
    if (!dFrom || !dTo) {
      alert("Geçerli başlangıç/bitiş tarihi seçiniz (YYYY-AA-GG).");
      return;
    }
    setStarted(true);

    const pSnap = await getDocs(collection(db, "personeller"));
    setPersoneller(
      pSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))
    );

    const iQuery = query(
      collection(db, "issues"),
      where("date", ">=", toTimestamp(fromStr, false)),
      where("date", "<=", toTimestamp(toStr, true)),
      orderBy("date", "desc")
    );
    const iSnap = await getDocs(iQuery);
    setIssues(iSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));

    const kQuery = query(
      collection(db, "kazalar"),
      where("tarih", ">=", toTimestamp(fromStr, false)),
      where("tarih", "<=", toTimestamp(toStr, true)),
      orderBy("tarih", "desc")
    );
    const kSnap = await getDocs(kQuery);
    setKazalar(
      kSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter((k) => k.olayTuru !== "RAMAK_KALA")
    );

    const terminSnap = await getDocs(collection(db, "termin"));
    const tMap = {};
    terminSnap.forEach((d) => {
      tMap[d.id] = { id: d.id, ...(d.data() || {}) };
    });
    setTerminMap(tMap);

    const titleSnap = await getDocs(collection(db, "termin_title_map"));
    const ttMap = {};
    titleSnap.forEach((d) => {
      ttMap[d.id] = { id: d.id, ...(d.data() || {}) };
    });
    setTitleMap(ttMap);
  };

  // DOCX olarak indirme
  // DOCX olarak indirme (VERİLERİ TABLO OLARAK)
  // DOCX olarak indirme (grafikler resim + veriler Word tablosu)
  const handleExportDocx = async () => {
    if (!started) {
      alert("Önce raporu oluşturun (Raporu Başlat'a basın).");
      return;
    }

    try {
      // Grafikten görüntü almak için küçük yardımcı
      const captureChartImage = async (el) => {
        if (!el) return null;
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          scrollX: 0,
          scrollY: -window.scrollY,
        });
        const dataUrl = canvas.toDataURL("image/png");
        const base64 = dataUrl.split(",")[1];
        const bytes = Uint8Array.from(atob(base64), (c) =>
          c.charCodeAt(0)
        );
        const maxWidth = 600;
        const width =
          canvas.width > maxWidth ? maxWidth : canvas.width;
        const height = (canvas.height * width) / canvas.width;

        return new ImageRun({
          data: bytes,
          transformation: { width, height },
        });
      };

      const makeCell = (text) =>
        new DocxTableCell({
          children: [new Paragraph(String(text ?? ""))],
        });

      const makeHeaderRow = (cells) =>
        new DocxTableRow({
          children: cells.map((c) => makeCell(c)),
        });

      const sectionsChildren = [];

      /* ---- 1) EKSİKLİKLERİN TESİSLERE DAĞILIMI ---- */
      if (eksiklikTesisStack.length > 0) {
        sectionsChildren.push(
          new Paragraph({
            text: "EKSİKLİKLERİN TESİSLERE DAĞILIMI",
          })
        );

        const imgRun = await captureChartImage(eksiklikChartRef.current);
        if (imgRun) {
          sectionsChildren.push(
            new Paragraph({
              children: [imgRun],
            })
          );
        }

        const rows = [
          makeHeaderRow([
            "Tesis",
            "Çözülen",
            "Bekleyen",
            "Toplam",
            "Çözülen %",
            "Bekleyen %",
          ]),
          ...eksiklikTesisStack.map((r) =>
            new DocxTableRow({
              children: [
                makeCell(r.tesis),
                makeCell(r.solved),
                makeCell(r.open),
                makeCell(r.total),
                makeCell(r.solvedPct),
                makeCell(r.openPct),
              ],
            })
          ),
        ];

        sectionsChildren.push(
          new DocxTable({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows,
          })
        );
      }

      /* ---- 2) AÇIK EKSİKLİK YAŞLANMA ---- */
      if (agingSeries.length > 0) {
        sectionsChildren.push(
          new Paragraph({
            text: "Açık Eksiklik Yaşlanma (Tesis Bazında)",
          })
        );

        const imgRun = await captureChartImage(agingChartRef.current);
        if (imgRun) {
          sectionsChildren.push(
            new Paragraph({
              children: [imgRun],
            })
          );
        }

        const rows = [
          makeHeaderRow(["Tesis", "<30", "30-59", "60-89", ">=90", "Toplam"]),
          ...agingSeries.map((r) =>
            new DocxTableRow({
              children: [
                makeCell(r.tesis),
                makeCell(r.lt30 || 0),
                makeCell(r.d30_59 || 0),
                makeCell(r.d60_89 || 0),
                makeCell(r.p90 || 0),
                makeCell(r.total || 0),
              ],
            })
          ),
        ];

        sectionsChildren.push(
          new DocxTable({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows,
          })
        );
      }

      /* ---- 3) TESİSLERE GÖRE EKSİKLİKLER (AÇIK) ---- */
      if (tesisEksiklikDetay.length > 0) {
        sectionsChildren.push(
          new Paragraph({
            text:
              tesisSel === "TÜMÜ"
                ? "TESİSLERE GÖRE EKSİKLİKLER (Açık)"
                : `${tesisSel} TESİSİ EKSİKLİKLERİ (Açık)`,
          })
        );

        const rows = [
          makeHeaderRow([
            "Tesis",
            "Eksiklik",
            "Plaka",
            "Geçen Süre (gün)",
            "Termin Süresi (gün)",
          ]),
          ...tesisEksiklikDetay.map((r) =>
            new DocxTableRow({
              children: [
                makeCell(r.tesis),
                makeCell(r.issue),
                makeCell(r.plate),
                makeCell(
                  r.daysOpen != null ? r.daysOpen : "-"
                ),
                makeCell(
                  r.terminDays != null ? r.terminDays : "-"
                ),
              ],
            })
          ),
        ];

        sectionsChildren.push(
          new DocxTable({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows,
          })
        );
      }

      /* ---- 4) KAZALARIN AYLARA DAĞILIMI (TESİS BAZLI) ---- */
      if (kazaByMonthTesis.table.length > 0) {
        sectionsChildren.push(
          new Paragraph({
            text: "Kazaların Aylara Dağılımı (Tesis Bazlı)",
          })
        );

        const imgRun = await captureChartImage(kazaAyChartRef.current);
        if (imgRun) {
          sectionsChildren.push(
            new Paragraph({
              children: [imgRun],
            })
          );
        }

        const monthKeys = kazaByMonthTesis.chart.map((m) => m.ay);

        const rows = [
          makeHeaderRow(["Tesis", ...monthKeys, "Genel Toplam"]),
          ...kazaByMonthTesis.table.map((r) =>
            new DocxTableRow({
              children: [
                makeCell(r.tesis),
                ...monthKeys.map((m) => makeCell(r[m] || 0)),
                makeCell(r.total || 0),
              ],
            })
          ),
        ];

        sectionsChildren.push(
          new DocxTable({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows,
          })
        );
      }

      /* ---- 5) EN ÇOK KAZA YAPAN 10 PERSONEL ---- */
      if (top10Kaza.length > 0) {
        sectionsChildren.push(
          new Paragraph({
            text: "En Çok Kaza Yapan 10 Personel",
          })
        );

        const rows = [
          makeHeaderRow([
            "Ad Soyad",
            "Kaza Sayısı",
            "Toplam Çalıştığı Gün",
          ]),
          ...top10Kaza.map((r) =>
            new DocxTableRow({
              children: [
                makeCell(r.personel),
                makeCell(r.adet),
                makeCell(r.totalDays || 0),
              ],
            })
          ),
        ];

        sectionsChildren.push(
          new DocxTable({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows,
          })
        );
      }

      /* ---- 6) KAZALARIN BAŞLIKLARA DAĞILIMI ---- */
      if (kazaBaslikTable.length > 0) {
        sectionsChildren.push(
          new Paragraph({
            text: "Kazaların Başlıklara Dağılımı",
          })
        );

        const rows = [
          makeHeaderRow(["Kaza Başlığı", "Kaza Sayısı"]),
          ...kazaBaslikTable.map((r) =>
            new DocxTableRow({
              children: [makeCell(r.baslik), makeCell(r.adet)],
            })
          ),
        ];

        sectionsChildren.push(
          new DocxTable({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows,
          })
        );
      }

      /* ---- 7) KAZALARIN LİSTESİ ---- */
      if (kazaDetayRows.length > 0) {
        sectionsChildren.push(
          new Paragraph({
            text: "Kazaların Listesi",
          })
        );

        const rows = [
          makeHeaderRow([
            "Tesis",
            "Tarih",
            "Saat",
            "Plaka",
            "Personel",
            "Kaza Başlığı",
            "Açıklama",
            "Çalıştığı Gün",
          ]),
          ...kazaDetayRows.map((r) =>
            new DocxTableRow({
              children: [
                makeCell(r.tesis),
                makeCell(r.tarih),
                makeCell(r.time),
                makeCell(r.plaka),
                makeCell(r.personel),
                makeCell(r.baslik),
                makeCell(r.aciklama),
                makeCell(r.worked || 0),
              ],
            })
          ),
        ];

        sectionsChildren.push(
          new DocxTable({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows,
          })
        );
      }

      /* ---- 8) PERSONEL DURUMUMUZ ---- */
      sectionsChildren.push(
        new Paragraph({ text: "PERSONEL DURUMUMUZ" })
      );

      sectionsChildren.push(
        new Paragraph({
          text: `Toplam: ${personelDurum.toplam} | Giriş: ${personelDurum.giris} | Çıkış: ${personelDurum.cikis}`,
        })
      );

      if (personelDurum.byTesis.length > 0) {
        sectionsChildren.push(
          new Paragraph({ text: "Tesis Bazında Personel" })
        );
        const rows = [
          makeHeaderRow(["Tesis", "Kişi Sayısı"]),
          ...personelDurum.byTesis.map((t) =>
            new DocxTableRow({
              children: [makeCell(t.tesis), makeCell(t.count)],
            })
          ),
        ];
        sectionsChildren.push(
          new DocxTable({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows,
          })
        );
      }

      if (personelDurum.byGorev.length > 0) {
        sectionsChildren.push(
          new Paragraph({ text: "Görev Bazında Personel" })
        );
        const rows = [
          makeHeaderRow(["Görev", "Kişi Sayısı"]),
          ...personelDurum.byGorev.map((g) =>
            new DocxTableRow({
              children: [makeCell(g.gorev), makeCell(g.count)],
            })
          ),
        ];
        sectionsChildren.push(
          new DocxTable({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows,
          })
        );
      }

      /* ---- 9) GİRİŞ VE ÇIKIŞ YAPAN PERSONELLER ---- */
      if (girisCikisRows.length > 0) {
        sectionsChildren.push(
          new Paragraph({
            text: "Giriş ve Çıkış Yapan Personeller",
          })
        );

        const rows = [
          makeHeaderRow([
            "Tesis",
            "Görev",
            "Ad Soyad",
            "Çalıştığı Gün",
            "Giriş Tarihleri",
            "Çıkış Tarihleri",
          ]),
          ...girisCikisRows.map((r) =>
            new DocxTableRow({
              children: [
                makeCell(r.tesis),
                makeCell(r.gorev),
                makeCell(r.adsoyad),
                makeCell(r.worked),
                makeCell(r.girisStr),
                makeCell(r.cikisStr),
              ],
            })
          ),
        ];

        sectionsChildren.push(
          new DocxTable({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows,
          })
        );
      }

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: sectionsChildren,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, "AylikIsgRaporu.docx");
    } catch (err) {
      console.error("DOCX export error:", err);
      alert(
        "Word dosyası oluşturulurken bir hata oluştu.\n" +
          (err?.message ? `Detay: ${err.message}` : "")
      );
    }
  };



  /* --------- Tesis seti --------- */
  const tesisSet = useMemo(() => {
    const s = new Set();

    personellers.forEach((p) =>
      s.add((p.tesis || "GENEL").toString().toUpperCase())
    );
    issues.forEach((i) =>
      s.add((i.tesis || "GENEL").toString().toUpperCase())
    );
    kazalar.forEach((k) =>
      s.add((k.tesis || "GENEL").toString().toUpperCase())
    );

    s.delete(""); // boş olanı at

    // DÖNEN ŞEY **ARRAY**, o yüzden .map kullanılabiliyor
    return Array.from(s).sort((a, b) => a.localeCompare(b, "tr"));
  }, [personellers, issues, kazalar]);


  /* --------- Personel lookup --------- */
  const personByName = useMemo(() => {
    const m = new Map();
    personellers.forEach((p) => {
      const key = `${(p.ad || "").trim()} ${(p.soyad || "").trim()}`
        .trim()
        .toLowerCase();
      if (key) m.set(key, p);
    });
    return m;
  }, [personellers]);

  /* ================= 1) EKSİKLİKLERİN TESİSLERE DAĞILIMI ================= */
  const eksiklikTesisStack = useMemo(() => {
    const map = new Map();
    tesisSet.forEach((t) => map.set(t, { solved: 0, open: 0, total: 0 }));

    issues.forEach((it) => {
      const tesis = (it.tesis || "GENEL").toString().toUpperCase();
      if (!map.has(tesis)) map.set(tesis, { solved: 0, open: 0, total: 0 });
      const entry = map.get(tesis);
      if (it.resolved) entry.solved += 1;
      else entry.open += 1;
      entry.total += 1;
    });

    const rows = Array.from(map.entries())
      .map(([tesis, v]) => ({
        tesis,
        solved: v.solved,
        open: v.open,
        total: v.total,
        solvedPct: v.total
          ? Math.round((v.solved * 1000) / v.total) / 10
          : 0,
        openPct: v.total
          ? Math.round((v.open * 1000) / v.total) / 10
          : 0,
      }))
      .filter((r) => r.total > 0)
      .filter((r) => (tesisSel === "TÜMÜ" ? true : r.tesis === tesisSel))
      .sort((a, b) => a.tesis.localeCompare(b.tesis, "tr"));

    return rows;
  }, [issues, tesisSet, tesisSel]);

  /* ================= 2) AÇIK EKSİKLİK YAŞLANMA ================= */
  const agingBuckets = [
    { key: "lt30", name: "<30", color: COLORS.agingLt30 },
    { key: "d30_59", name: "30-59", color: COLORS.aging30_59 },
    { key: "d60_89", name: "60-89", color: COLORS.aging60_89 },
    { key: "p90", name: ">=90", color: COLORS.aging90p },
  ];

  const agingSeries = useMemo(() => {
    const dFrom = toDateSafe(fromStr, false);
    const dTo = toDateSafe(toStr, true);
    if (!dFrom || !dTo) return [];

    const today = new Date();
    const base = new Map();
    tesisSet.forEach((t) =>
      base.set(t, { tesis: t, lt30: 0, d30_59: 0, d60_89: 0, p90: 0 })
    );

    issues.forEach((it) => {
      if (it.resolved) return;
      if (!inRange(it.date, dFrom, dTo)) return;
      const tesis = (it.tesis || "GENEL").toString().toUpperCase();
      if (tesisSel !== "TÜMÜ" && tesisSel !== tesis) return;

      const ms = tsToMs(it.date);
      if (!Number.isFinite(ms)) return;
      const diff = Math.max(
        0,
        Math.floor((today.getTime() - ms) / 86400000)
      );
      const row =
        base.get(tesis) || { tesis, lt30: 0, d30_59: 0, d60_89: 0, p90: 0 };
      if (diff < 30) row.lt30 += 1;
      else if (diff < 60) row.d30_59 += 1;
      else if (diff < 90) row.d60_89 += 1;
      else row.p90 += 1;
      base.set(tesis, row);
    });

    const rows = Array.from(base.values())
      .map((r) => ({
        ...r,
        total:
          (r.lt30 || 0) +
          (r.d30_59 || 0) +
          (r.d60_89 || 0) +
          (r.p90 || 0),
      }))
      .filter((r) => r.total > 0)
      .filter((r) => (tesisSel === "TÜMÜ" ? true : r.tesis === tesisSel));

    return rows.sort((a, b) => a.tesis.localeCompare(b.tesis, "tr"));
  }, [issues, fromStr, toStr, tesisSel, tesisSet]);

  /* ================= 3) TESİS EKSİKLİK DETAY (AÇIK) ================= */
  const tesisEksiklikDetay = useMemo(() => {
    const dFrom = toDateSafe(fromStr, false);
    const dTo = toDateSafe(toStr, true);
    if (!dFrom || !dTo) return [];

    const rawRows = issues
      .filter((it) => !it.resolved && inRange(it.date, dFrom, dTo))
      .filter((it) =>
        tesisSel === "TÜMÜ"
          ? true
          : (it.tesis || "").toUpperCase() === tesisSel
      )
      .map((it) => {
        const tarih =
          it.date?.toDate?.()?.toLocaleDateString("tr-TR") || "-";
        const tesis = (it.tesis || "GENEL").toString().toUpperCase();
        const plate = it.plate || "-";
        const issue = it.issue || "-";

        const endDate = toDateSafe(toStr, true);
        const daysOpen =
          endDate && it.date?.toDate
            ? Math.max(0, daysBetween(it.date.toDate(), endDate))
            : null;

        const cat = getCategoryForIssue(it);
        const terminDays =
          typeof cat?.days === "number" ? cat.days : null;

        const isLate =
          terminDays != null && Number.isFinite(daysOpen)
            ? daysOpen > terminDays
            : null;

        return {
          tesis,
          tarih,
          issue,
          plate,
          daysOpen: Number.isFinite(daysOpen) ? daysOpen : null,
          terminDays,
          isLate,
        };
      });

    const grouped = new Map();
    rawRows.forEach((r) => {
      if (!grouped.has(r.tesis)) grouped.set(r.tesis, []);
      grouped.get(r.tesis).push(r);
    });

    const sortedTesis = Array.from(grouped.keys()).sort((a, b) =>
      a.localeCompare(b, "tr")
    );

    const withSpan = [];
    sortedTesis.forEach((t) => {
      const arr = grouped.get(t).sort((a, b) =>
        a.issue.localeCompare(b.issue, "tr")
      );
      arr.forEach((r, idx) =>
        withSpan.push({
          ...r,
          rowSpan: idx === 0 ? arr.length : 0,
        })
      );
    });

    return withSpan;
  }, [issues, fromStr, toStr, tesisSel, terminMap, titleMap]);

  /* ================= 4) KAZALARIN AYLARA DAĞILIMI (TESİS BAZLI) ================= */
  const kazaByMonthTesis = useMemo(() => {
    const dFrom = toDateSafe(fromStr, false);
    const dTo = toDateSafe(toStr, true);
    if (!dFrom || !dTo)
      return { chart: [], table: [], months: [], tesis: [] };

    const months = [];
    const cur = new Date(dFrom.getFullYear(), dFrom.getMonth(), 1);
    while (cur <= dTo) {
      months.push(new Date(cur));
      cur.setMonth(cur.getMonth() + 1);
    }

    const bucket = new Map();
    months.forEach((m) => {
      bucket.set(monthKey(m), { ay: monthKey(m) });
    });

    const allTesis = new Set();

    kazalar.forEach((k) => {
      const dt = k.tarih?.toDate?.() || toDateMaybe(k.tarih);
      if (!dt) return;
      if (dt < dFrom || dt > dTo) return;

      const key = monthKey(dt);

      const nameKey = (k.personel || "").trim().toLowerCase();
      const p = nameKey ? personByName.get(nameKey) : null;
      let tesis;
      if (p && p.tesis) {
        tesis = p.tesis.toString().toUpperCase();
      } else {
        tesis = (k.tesis || "GENEL").toString().toUpperCase();
      }

      if (tesisSel !== "TÜMÜ" && tesisSel !== tesis) return;

      allTesis.add(tesis);

      const row = bucket.get(key) || { ay: key };
      row[tesis] = (row[tesis] || 0) + 1;
      bucket.set(key, row);
    });

    const chart = Array.from(bucket.values()).sort((a, b) =>
      a.ay < b.ay ? -1 : 1
    );

    const tesisList = Array.from(allTesis)
      .map((t) => {
        let total = 0;
        chart.forEach((m) => {
          total += m[t] || 0;
        });
        return { t, total };
      })
      .filter((x) => x.total > 0)
      .sort((a, b) => a.t.localeCompare(b.t, "tr"))
      .map((x) => x.t);

    const table = tesisList.map((t) => {
      const row = { tesis: t, total: 0 };
      chart.forEach((m) => {
        row[m.ay] = m[t] || 0;
        row.total += row[m.ay];
      });
      return row;
    });

    const sameMonths2024Total = months.map((m) => ({
      ay: monthKey(m).replace(String(m.getFullYear()), "2024"),
    }));

    return { chart, table, months, tesis: tesisList, sameMonths2024Total };
  }, [kazalar, fromStr, toStr, personByName, tesisSel]);

  /* ================= 5) EN ÇOK KAZA YAPAN 10 PERSONEL ================= */
  const top10Kaza = useMemo(() => {
    const dFrom = toDateSafe(fromStr, false);
    const dTo = toDateSafe(toStr, true);
    if (!dFrom || !dTo) return [];

    const counts = new Map();

    kazalar.forEach((k) => {
      if (!inRange(k.tarih, dFrom, dTo)) return;
      const nameRaw = k.personel || "-";
      const nameKey = nameRaw.trim().toLowerCase();
      if (!nameKey) return;
      const p = personByName.get(nameKey);
      const totalDays = p ? totalWorkedDaysAllRanges(p) : 0;
      const prev =
        counts.get(nameKey) || {
          personel: nameRaw || "-",
          adet: 0,
          totalDays,
        };
      prev.adet += 1;
      prev.totalDays = totalDays;
      counts.set(nameKey, prev);
    });

    return Array.from(counts.values())
      .sort((a, b) => b.adet - a.adet)
      .slice(0, 10)
      .map((r) => ({
        personel: titleCaseTR(r.personel),
        adet: r.adet,
        totalDays: r.totalDays,
      }));
  }, [kazalar, fromStr, toStr, personByName]);

  /* ================= 6) KAZALARIN BAŞLIKLARA DAĞILIMI ================= */
  const kazaBaslikTable = useMemo(() => {
    const dFrom = toDateSafe(fromStr, false);
    const dTo = toDateSafe(toStr, true);
    if (!dFrom || !dTo) return [];
    const by = new Map();
    kazalar.forEach((k) => {
      if (!inRange(k.tarih, dFrom, dTo)) return;
      const key = k.kazaBasligi || "-";
      by.set(key, (by.get(key) || 0) + 1);
    });
    const arr = Array.from(by.entries())
      .map(([baslik, adet]) => ({ baslik, adet }))
      .sort((a, b) => b.adet - a.adet);
    return arr;
  }, [kazalar, fromStr, toStr]);

  /* ================= 7) KAZALARIN LİSTESİ ================= */
  const kazaDetayRows = useMemo(() => {
    const dFrom = toDateSafe(fromStr, false);
    const dTo = toDateSafe(toStr, true);
    if (!dFrom || !dTo) return [];
    return kazalar
      .filter((k) => inRange(k.tarih, dFrom, dTo))
      .map((k) => {
        const tarih =
          k.tarih?.toDate?.()?.toLocaleDateString("tr-TR") || "-";
        const time = k.saat || "-";
        const plaka = k.plaka || "-";
        const personel = k.personel || "-";

        let tesis = (k.tesis || "-").toString().toUpperCase();
        let worked = "-";
        if (personel) {
          const nameKey = personel.trim().toLowerCase();
          const p = personByName.get(nameKey);
          if (p) {
            tesis = (p.tesis || "-").toString().toUpperCase();
            worked = totalWorkedDaysAllRanges(p);
          }
        }

        const baslik = k.kazaBasligi || "-";
        const aciklama = k.aciklama || "-";

        return {
          tesis,
          tarih,
          time,
          plaka,
          personel,
          baslik,
          aciklama,
          worked,
        };
      });
  }, [kazalar, fromStr, toStr, personByName]);

  /* ================= 8) PERSONEL DURUMUMUZ ================= */
  const personelDurum = useMemo(() => {
    const dFrom = toDateSafe(fromStr, false);
    const dTo = toDateSafe(toStr, true);
    if (!dFrom || !dTo)
      return { toplam: 0, giris: 0, cikis: 0, byTesis: [], byGorev: [] };

    let aktif = [];
    const byTesis = new Map();
    const byGorev = new Map();

    personellers.forEach((p) => {
      const worked = workedDaysForPersonRanges(p, dFrom, dTo);
      if (worked > 0) {
        aktif.push(p);
        const t = (p.tesis || "GENEL").toString().toUpperCase();
        byTesis.set(t, (byTesis.get(t) || 0) + 1);
        const g = (p.gorev || "GENEL").toString().toUpperCase();
        byGorev.set(g, (byGorev.get(g) || 0) + 1);
      }
    });

    let giris = 0,
      cikis = 0;
    for (const p of personellers) {
      const tarih = p?.tarih || {};
      for (let i = 1; i <= 9; i++) {
        const g = toDateMaybe(tarih[`giris${i}`]);
        const c = toDateMaybe(tarih[`cikis${i}`]);
        if (g && g >= dFrom && g <= dTo) giris++;
        if (c && c >= dFrom && c <= dTo) cikis++;
      }
    }

    const tArr = Array.from(byTesis.entries())
      .map(([tesis, count]) => ({ tesis, count }))
      .sort((a, b) => a.tesis.localeCompare(b.tesis, "tr"));
    const gArr = Array.from(byGorev.entries())
      .map(([gorev, count]) => ({ gorev, count }))
      .sort((a, b) => a.gorev.localeCompare(b.gorev, "tr"));

    return {
      toplam: aktif.length,
      giris,
      cikis,
      byTesis: tArr,
      byGorev: gArr,
    };
  }, [personellers, fromStr, toStr]);

  /* ================= 9) GİRİŞ/ÇIKIŞ LİSTESİ ================= */
  const girisCikisRows = useMemo(() => {
    const dFrom = toDateSafe(fromStr, false);
    const dTo = toDateSafe(toStr, true);
    if (!dFrom || !dTo) return [];

    const rows = [];
    for (const p of personellers) {
      const tesis = (p.tesis || "GENEL").toString().toUpperCase();
      const gorev = (p.gorev || "-").toString().toUpperCase();
      const adsoyad = `${p.ad || ""} ${p.soyad || ""}`.trim();
      const worked = workedDaysForPersonRanges(p, dFrom, dTo);

      const tarih = p?.tarih || {};
      const girisList = [];
      const cikisList = [];
      for (let i = 1; i <= 9; i++) {
        const g = toDateMaybe(tarih[`giris${i}`]);
        const c = toDateMaybe(tarih[`cikis${i}`]);
        if (g && g >= dFrom && g <= dTo)
          girisList.push(g.toLocaleDateString("tr-TR"));
        if (c && c >= dFrom && c <= dTo)
          cikisList.push(c.toLocaleDateString("tr-TR"));
      }

      if (girisList.length === 0 && cikisList.length === 0) continue;
      rows.push({
        tesis,
        gorev,
        adsoyad: titleCaseTR(adsoyad),
        worked,
        girisStr: girisList.join("\n") || "-",
        cikisStr: cikisList.join("\n") || "-",
        type:
          cikisList.length > 0 && girisList.length === 0
            ? "cikis"
            : girisList.length > 0 && cikisList.length === 0
            ? "giris"
            : "both",
      });
    }

    const grouped = new Map();
    rows.forEach((r) => {
      if (!grouped.has(r.tesis)) grouped.set(r.tesis, []);
      grouped.get(r.tesis).push(r);
    });

    const sortedTesis = Array.from(grouped.keys()).sort((a, b) =>
      a.localeCompare(b, "tr")
    );

    const withSpan = [];
    sortedTesis.forEach((t) => {
      const arr = grouped
        .get(t)
        .slice()
        .sort((a, b) => a.adsoyad.localeCompare(b.adsoyad, "tr"));
      arr.forEach((r, idx) => {
        withSpan.push({
          ...r,
          rowSpan: idx === 0 ? arr.length : 0,
        });
      });
    });

    return withSpan;
  }, [personellers, fromStr, toStr]);

  /* ======================= Küçük UI parçaları ======================= */
  const Table = ({ children, style }) => (
    <table
      style={{
        borderCollapse: "collapse",
        width: "100%",
        fontSize: 12,
        ...style,
      }}
    >
      {children}
    </table>
  );
  const Th = ({ children, style, align = "left" }) => (
    <th
      style={{
        padding: "6px 8px",
        border: COLORS.dashedBorder,
        textAlign: align,
        fontWeight: 700,
        ...style,
      }}
    >
      {children}
    </th>
  );
  const Td = ({ children, style, align = "left", rowSpan, colSpan }) => (
    <td
      rowSpan={rowSpan}
      colSpan={colSpan}
      style={{
        padding: "6px 8px",
        border: COLORS.dashedBorder,
        textAlign: align,
        whiteSpace: "pre-line",
        ...style,
      }}
    >
      {children}
    </td>
  );

  return (
    <div style={{ padding: 12 }}>
      {/* Filtreler */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "end",
          background: "#f8fafc",
          border: `1px solid ${COLORS.panelBorder}`,
          borderRadius: 10,
          padding: 10,
          marginBottom: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 12 }}>Başlangıç</div>
          <input
            type="date"
            value={fromStr}
            onChange={(e) => setFromStr(e.target.value)}
          />
        </div>
        <div>
          <div style={{ fontSize: 12 }}>Bitiş</div>
          <input
            type="date"
            value={toStr}
            onChange={(e) => setToStr(e.target.value)}
          />
        </div>
        <div>
          <div style={{ fontSize: 12 }}>Tesis</div>
          <select
            value={tesisSel}
            onChange={(e) => setTesisSel(e.target.value)}
          >
            <option value="TÜMÜ">TÜMÜ</option>
            {tesisSet.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleStart}
          style={{
            marginLeft: "auto",
            background: "#111827",
            color: "#fff",
            fontWeight: 700,
            padding: "8px 12px",
            borderRadius: 8,
            border: 0,
          }}
        >
          ▶️ Raporu Başlat
        </button>
        <button
          onClick={handleExportDocx}
          disabled={!started}
          style={{
            background: started ? "#1d4ed8" : "#9ca3af",
            color: "#fff",
            fontWeight: 700,
            padding: "8px 12px",
            borderRadius: 8,
            border: 0,
            cursor: started ? "pointer" : "not-allowed",
          }}
        >
          ⬇️ DOCX olarak indir
        </button>
      </div>

      {!started ? (
        <div
          style={{
            border: `1px dashed ${COLORS.panelBorder}`,
            borderRadius: 10,
            padding: 16,
            color: "#6b7280",
          }}
        >
          Filtreleri seçin ve <b>Raporu Başlat</b>'a basın.
        </div>
      ) : (
        // DOCX'e aynen gidecek rapor alanı
        <div ref={reportRef}>
          {/* 1) Eksikliklerin Tesislere Dağılımı */}
          {eksiklikTesisStack.length > 0 && (
            <section
              style={{
                background: COLORS.panel,
                border: `1px solid ${COLORS.panelBorder}`,
                borderRadius: 12,
                padding: 12,
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0, marginBottom: 6 }}>
                E K S İ K L İ K L E R İ N T E S İ S L E R E D A Ğ I L I M I
              </h3>

              <div
                ref={eksiklikChartRef}
                style={{
                  width: "100%",
                  height: 40 + eksiklikTesisStack.length * 40,
                }}
              >

                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    layout="vertical"
                    data={eksiklikTesisStack}
                    margin={{ top: 10, right: 20, bottom: 10, left: 80 }}
                  >
                    <CartesianGrid stroke={COLORS.grid} />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="tesis" />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="solved"
                      name="Çözülen"
                      stackId="a"
                      fill={COLORS.tesisSolved}
                    />
                    <Bar
                      dataKey="open"
                      name="Bekleyen"
                      stackId="a"
                      fill={COLORS.tesisOpen}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}

          {/* 2) Açık Eksiklik Yaşlanma */}
          <section
            style={{
              background: COLORS.panel,
              border: `1px solid ${COLORS.panelBorder}`,
              borderRadius: 12,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 6 }}>
              Açık Eksiklik Yaşlanma Grafiği
            </h3>
            <div ref={agingChartRef} style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={agingSeries}
                  margin={{ top: 10, right: 20, bottom: 40, left: 20 }}
                >
                  <CartesianGrid stroke={COLORS.grid} />
                  <XAxis dataKey="tesis" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="lt30"
                    name="<30"
                    fill={agingBuckets[0].color}
                  />
                  <Bar
                    dataKey="d30_59"
                    name="30-59"
                    fill={agingBuckets[1].color}
                  />
                  <Bar
                    dataKey="d60_89"
                    name="60-89"
                    fill={agingBuckets[2].color}
                  />
                  <Bar
                    dataKey="p90"
                    name=">=90"
                    fill={agingBuckets[3].color}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* 3) TESİSLERE GÖRE EKSİKLİKLER (Açık) */}
          <section
            style={{
              background: COLORS.panel,
              border: `1px solid ${COLORS.panelBorder}`,
              borderRadius: 12,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 6 }}>
              {tesisSel === "TÜMÜ"
                ? "TESİSLERE GÖRE EKSİKLİKLER (Açık)"
                : `${tesisSel} TESİSİ EKSİKLİKLERİ (Açık)`}
            </h3>
            <div style={{ overflowX: "auto" }}>
              <Table>
                <thead>
                  <tr style={{ background: COLORS.headerBlue }}>
                    <Th>Tesis</Th>
                    <Th>Eksiklik</Th>
                    <Th>Plaka</Th>
                    <Th align="right">Geçen Süre (gün)</Th>
                    <Th align="right">Termin Süresi (gün)</Th>
                  </tr>
                </thead>
                <tbody>
                  {tesisEksiklikDetay.map((r, i) => {
                    const bg =
                      r.isLate == null
                        ? "transparent"
                        : r.isLate
                        ? "#fee2e2"
                        : "#dcfce7";
                    return (
                      <tr key={i} style={{ background: bg }}>
                        {r.rowSpan > 0 && (
                          <Td
                            rowSpan={r.rowSpan}
                            style={{
                              textAlign: "center",
                              fontWeight: 700,
                              verticalAlign: "top",
                            }}
                          >
                            {r.tesis}
                          </Td>
                        )}
                        <Td
                          style={{ textAlign: "center", fontWeight: 600 }}
                        >
                          {r.issue}
                        </Td>
                        <Td>{r.plate}</Td>
                        <Td align="right">
                          {r.daysOpen != null ? fmt0(r.daysOpen) : "-"}
                        </Td>
                        <Td align="right">
                          {r.terminDays != null ? fmt0(r.terminDays) : "-"}
                        </Td>
                      </tr>
                    );
                  })}
                  {tesisEksiklikDetay.length === 0 && (
                    <tr>
                      <Td colSpan={5} style={{ color: "#6b7280" }}>
                        Kayıt yok
                      </Td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </section>

          {/* 4) Kazaların Aylara Dağılımı (tesis bazlı) */}
          <section
            style={{
              background: COLORS.panel,
              border: `1px solid ${COLORS.panelBorder}`,
              borderRadius: 12,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 6 }}>
              Kazaların Aylara Dağılımı
            </h3>
            <div ref={kazaAyChartRef} style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={kazaByMonthTesis.chart}>
                  <CartesianGrid stroke={COLORS.grid} />
                  <XAxis
                    dataKey="ay"
                    tickFormatter={(k) => {
                      const [y, m] = k.split("-");
                      return `${y}-${m}`;
                    }}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  {kazaByMonthTesis.tesis.map((t, idx) => (
                    <Bar
                      key={t}
                      dataKey={t}
                      name={t}
                      stackId="a"
                      fill={[
                        "#1d4ed8",
                        "#16a34a",
                        "#dc2626",
                        "#0d9488",
                        "#f97316",
                        "#6366f1",
                        "#ec4899",
                        "#22c55e",
                        "#a855f7",
                        "#ef4444",
                        "#14b8a6",
                        "#eab308",
                        "#3b82f6",
                        "#facc15",
                        "#0f766e",
                        "#7c3aed",
                      ][idx % 16]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={{ marginTop: 10, overflowX: "auto" }}>
              <Table>
                <thead>
                  <tr style={{ background: COLORS.headerBlue }}>
                    <Th>Tesis</Th>
                    {kazaByMonthTesis.chart.map((m) => (
                      <Th key={m.ay} align="right">
                        {m.ay}
                      </Th>
                    ))}
                    <Th align="right">Genel Toplam</Th>
                  </tr>
                </thead>
                <tbody>
                  {kazaByMonthTesis.table.map((r) => (
                    <tr key={r.tesis}>
                      <Td>{r.tesis}</Td>
                      {kazaByMonthTesis.chart.map((m) => (
                        <Td key={m.ay} align="right">
                          {fmt0(r[m.ay] || 0)}
                        </Td>
                      ))}
                      <Td align="right" style={{ fontWeight: 700 }}>
                        {fmt0(r.total)}
                      </Td>
                    </tr>
                  ))}
                  <tr>
                    <Td
                      colSpan={kazaByMonthTesis.chart.length + 2}
                      style={{ background: "#f1f5f9" }}
                    >
                      2024 yılı aynı aylara ait genel toplam (bilgi satırı):{" "}
                      {kazaByMonthTesis.chart
                        .map((m) =>
                          m.ay.replace(m.ay.slice(0, 4), "2024")
                        )
                        .join(", ")}
                    </Td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </section>

          {/* 5) En Çok Kaza Yapan 10 Personel */}
          <section
            style={{
              background: COLORS.panel,
              border: `1px solid ${COLORS.panelBorder}`,
              borderRadius: 12,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 6 }}>
              En Çok Kaza Yapan 10 Personel
            </h3>
            <div style={{ overflowX: "auto" }}>
              <Table>
                <thead>
                  <tr style={{ background: COLORS.headerBlue }}>
                    <Th>Ad Soyad</Th>
                    <Th align="right">Kaza Sayısı</Th>
                    <Th align="right">Toplam Çalıştığı Gün</Th>
                  </tr>
                </thead>
                <tbody>
                  {top10Kaza.map((r, i) => (
                    <tr key={i}>
                      <Td>{r.personel}</Td>
                      <Td align="right" style={{ fontWeight: 700 }}>
                        {fmt0(r.adet)}
                      </Td>
                      <Td align="right">{fmt0(r.totalDays || 0)}</Td>
                    </tr>
                  ))}
                  {top10Kaza.length === 0 && (
                    <tr>
                      <Td colSpan={3} style={{ color: "#6b7280" }}>
                        Kayıt yok
                      </Td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </section>

          {/* 6) Kazaların Başlıklara Dağılımı */}
          <section
            style={{
              background: COLORS.panel,
              border: `1px solid ${COLORS.panelBorder}`,
              borderRadius: 12,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 6 }}>
              Kazaların Başlıklara Dağılımı
            </h3>
            <div style={{ overflowX: "auto" }}>
              <Table>
                <thead>
                  <tr
                    style={{
                      background: COLORS.greenHeader,
                      color: "#fff",
                    }}
                  >
                    <Th>Kaza Başlığı</Th>
                    <Th align="right">Kaza Sayısı</Th>
                  </tr>
                </thead>
                <tbody>
                  {kazaBaslikTable.map((r, i) => (
                    <tr key={i}>
                      <Td>{r.baslik}</Td>
                      <Td align="right" style={{ fontWeight: 700 }}>
                        {fmt0(r.adet)}
                      </Td>
                    </tr>
                  ))}
                  <tr>
                    <Td
                      style={{
                        background: COLORS.greenTotal,
                        fontWeight: 700,
                      }}
                    >
                      Genel Toplam
                    </Td>
                    <Td
                      align="right"
                      style={{
                        background: COLORS.greenTotal,
                        fontWeight: 700,
                      }}
                    >
                      {fmt0(
                        kazaBaslikTable.reduce(
                          (s, x) => s + (x.adet || 0),
                          0
                        )
                      )}
                    </Td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </section>

          {/* 7) Kazaların Listesi */}
          <section
            style={{
              background: COLORS.panel,
              border: `1px solid ${COLORS.panelBorder}`,
              borderRadius: 12,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 6 }}>
              Kazaların Listesi
            </h3>
            <div style={{ overflowX: "auto" }}>
              <Table>
                <thead>
                  <tr style={{ background: COLORS.headerBlue }}>
                    <Th>Tesis</Th>
                    <Th>Tarih</Th>
                    <Th>Saat</Th>
                    <Th>Plaka</Th>
                    <Th>Personel</Th>
                    <Th>Kaza Başlığı</Th>
                    <Th>Açıklama</Th>
                    <Th align="right">Çalıştığı Gün</Th>
                  </tr>
                </thead>
                <tbody>
                  {kazaDetayRows.map((r, i) => (
                    <tr key={i}>
                      <Td>{r.tesis}</Td>
                      <Td>{r.tarih}</Td>
                      <Td>{r.time}</Td>
                      <Td>{r.plaka}</Td>
                      <Td>{r.personel}</Td>
                      <Td>{r.baslik}</Td>
                      <Td>{r.aciklama}</Td>
                      <Td align="right">{fmt0(r.worked || 0)}</Td>
                    </tr>
                  ))}
                  {kazaDetayRows.length === 0 && (
                    <tr>
                      <Td colSpan={8} style={{ color: "#6b7280" }}>
                        Kayıt yok
                      </Td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </section>

          {/* 8) PERSONEL DURUMUMUZ */}
          <section
            style={{
              background: COLORS.panel,
              border: `1px solid ${COLORS.panelBorder}`,
              borderRadius: 12,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 6 }}>
              PERSONEL DURUMUMUZ
            </h3>
            <div style={{ marginBottom: 8, fontWeight: 700 }}>
              Toplam: {fmt0(personelDurum.toplam)} | Giriş:{" "}
              {fmt0(personelDurum.giris)} | Çıkış:{" "}
              {fmt0(personelDurum.cikis)}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <div>
                <div
                  style={{ fontWeight: 700, marginBottom: 6 }}
                >
                  Tesis Bazında
                </div>
                <ul
                  style={{ margin: 0, paddingLeft: 16 }}
                >
                  {personelDurum.byTesis.map((t) => (
                    <li key={t.tesis}>
                      {t.tesis}: {fmt0(t.count)}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div
                  style={{ fontWeight: 700, marginBottom: 6 }}
                >
                  Görev Bazında
                </div>
                <ul
                  style={{ margin: 0, paddingLeft: 16 }}
                >
                  {personelDurum.byGorev.map((g) => (
                    <li key={g.gorev}>
                      {g.gorev}: {fmt0(g.count)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* 9) Giriş & Çıkış Yapan Personeller */}
          <section
            style={{
              background: COLORS.panel,
              border: `1px solid ${COLORS.panelBorder}`,
              borderRadius: 12,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 6 }}>
              Giriş ve Çıkış Yapan Personeller
            </h3>
            <div style={{ overflowX: "auto" }}>
              <Table>
                <thead>
                  <tr style={{ background: COLORS.headerBlue }}>
                    <Th>Tesis</Th>
                    <Th>Görev</Th>
                    <Th>Ad Soyad</Th>
                    <Th align="right">Çalıştığı Gün</Th>
                    <Th>Giriş Tarihleri</Th>
                    <Th>Çıkış Tarihleri</Th>
                  </tr>
                </thead>
                <tbody>
                  {girisCikisRows.map((r, i) => (
                    <tr
                      key={i}
                      style={{
                        background:
                          r.type === "cikis"
                            ? COLORS.kiremit
                            : r.type === "giris"
                            ? COLORS.softYesil
                            : "transparent",
                      }}
                    >
                      {r.rowSpan > 0 ? (
                        <Td
                          rowSpan={r.rowSpan}
                          style={{
                            textAlign: "center",
                            fontWeight: 700,
                          }}
                        >
                          {r.tesis}
                        </Td>
                      ) : null}
                      <Td>{r.gorev}</Td>
                      <Td>{r.adsoyad}</Td>
                      <Td align="right">{fmt0(r.worked)}</Td>
                      <Td>{r.girisStr}</Td>
                      <Td>{r.cikisStr}</Td>
                    </tr>
                  ))}
                  {girisCikisRows.length === 0 && (
                    <tr>
                      <Td colSpan={6} style={{ color: "#6b7280" }}>
                        Kayıt yok
                      </Td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
